/**
 * Product Image Tabs Web Component
 * Handles tab switching with accordion-disclosure component for desktop layout
 * Mobile layout shows all tabs stacked vertically
 */
class ProductImageTabs extends HTMLElement {
  constructor() {
    super();
    this._currentIndex = 0;
    this._isTransitioning = false;
  }

  connectedCallback() {
    // Find which accordion is initially open
    const accordions = this.querySelectorAll('accordion-disclosure');
    accordions.forEach((accordion, index) => {
      if (accordion.classList.contains('accordion-open') || accordion.hasAttribute('data-open')) {
        this._currentIndex = index;
      }
    });
    
    this._setupTabListeners();
    this._updateAccordionLayout();
  }

  /**
   * Setup event listeners for accordion button clicks
   */
  _setupTabListeners() {
    const accordions = this.querySelectorAll('accordion-disclosure');
    
    accordions.forEach((accordion, index) => {
      const button = accordion.querySelector('.accordion-v2__button');
      if (button) {
        // Use capture phase to intercept before accordion's internal handler
        button.addEventListener('click', (event) => {
          // Always prevent default accordion behavior for product-image-tabs
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Prevent clicks on already-open accordions
          if (accordion.classList.contains('accordion-open')) {
            return false;
          }
          
          // Switch to the clicked tab
          if (!this._isTransitioning && index !== this._currentIndex) {
            this._switchTab(index);
          }
        }, true); // Use capture phase
      }
    });
  }

  /**
   * Switch to a different tab
   * @param {number} newIndex - Index of the tab to switch to
   */
  _switchTab(newIndex) {
    const images = this.querySelectorAll('.product-image-tabs__image-wrapper-item');
    const accordions = this.querySelectorAll('accordion-disclosure');
    
    if (newIndex < 0 || newIndex >= images.length || newIndex >= accordions.length) {
      return;
    }

    this._isTransitioning = true;
    const oldIndex = this._currentIndex;
    this._currentIndex = newIndex;

    // Update accordion open states
    this._updateAccordionStates(newIndex, oldIndex, accordions);

    // Update accordion layout to keep tabs below at bottom
    this._updateAccordionLayout();

    // Animate image change
    this._animateImageChange(oldIndex, newIndex, images);
  }

  /**
   * Update accordion layout to keep tabs below active one at bottom
   */
  _updateAccordionLayout() {
    const accordionItems = this.querySelectorAll('.product-image-tabs__accordion-item');
    
    accordionItems.forEach((item, index) => {
      item.classList.remove('product-image-tabs__accordion-item--below-active');
      
      if (index === this._currentIndex + 1) {
        item.classList.add('product-image-tabs__accordion-item--below-active');
      }
    });
  }

  /**
   * Update accordion open states
   * @param {number} activeIndex - Index of the active accordion
   * @param {number} oldIndex - Previous active index
   * @param {NodeList} accordions - All accordion elements
   */
  _updateAccordionStates(activeIndex, oldIndex, accordions) {
    const activeAccordion = accordions[activeIndex];
    
    // Close all other accordions and open the new one simultaneously
    accordions.forEach((accordion, index) => {
      if (index !== activeIndex && accordion.classList.contains('accordion-open')) {
        this._closeAccordionWithHeightAnimation(accordion);
      }
    });
    
    // Open new accordion at the same time as closing others
    if (activeAccordion && !activeAccordion.classList.contains('accordion-open')) {
      this._openAccordionWithHeightAnimation(activeAccordion);
    }

    // Reset transitioning flag after animation completes
    setTimeout(() => {
      this._isTransitioning = false;
    }, 300); // Match the accordion animation duration
  }

  /**
   * Close accordion with height-only animation (no opacity fade)
   * @param {HTMLElement} accordion - The accordion element to close
   */
  _closeAccordionWithHeightAnimation(accordion) {
    const contentWrapper = accordion.querySelector('.accordion-v2__content-wrapper');
    const content = accordion.querySelector('.accordion-v2__content');
    const button = accordion.querySelector('.accordion-v2__button');
    
    if (!contentWrapper || !content) return;
    
    button.setAttribute("aria-expanded", "false");
    accordion.classList.remove("accordion-open");
    
    const height = content.offsetHeight;
    contentWrapper.style.height = height + "px";
    contentWrapper.style.overflow = "hidden";
    
    requestAnimationFrame(() => {
      contentWrapper.style.transition = "height 300ms ease";
      contentWrapper.style.height = "0px";
      
      setTimeout(() => {
        contentWrapper.hidden = true;
        contentWrapper.style.height = "";
        contentWrapper.style.transition = "";
        contentWrapper.style.overflow = "";
      }, 300);
    });
  }

  /**
   * Open accordion with height-only animation (no opacity fade)
   * @param {HTMLElement} accordion - The accordion element to open
   */
  _openAccordionWithHeightAnimation(accordion) {
    const contentWrapper = accordion.querySelector('.accordion-v2__content-wrapper');
    const content = accordion.querySelector('.accordion-v2__content');
    const button = accordion.querySelector('.accordion-v2__button');
    
    if (!contentWrapper || !content) return;
    
    button.setAttribute("aria-expanded", "true");
    accordion.classList.add("accordion-open");
    contentWrapper.hidden = false;
    contentWrapper.style.overflow = "hidden";
    
    const height = content.offsetHeight;
    contentWrapper.style.height = "0px";
    
    requestAnimationFrame(() => {
      contentWrapper.style.transition = "height 300ms ease";
      contentWrapper.style.height = height + "px";
      
      setTimeout(() => {
        contentWrapper.style.transition = "";
        contentWrapper.style.overflow = "";
      }, 300);
    });
  }

  /**
   * Animate image change with cross-fade (new image fades in on top, old fades out)
   * @param {number} oldIndex - Previous tab index
   * @param {number} newIndex - New tab index
   * @param {NodeList} images - All image wrapper elements
   */
  _animateImageChange(oldIndex, newIndex, images) {
    const oldImage = images[oldIndex];
    const newImage = images[newIndex];

    if (!oldImage || !newImage) {
      return;
    }

    const oldImgElement = oldImage.querySelector('.product-image-tabs__image');
    const newImgElement = newImage.querySelector('.product-image-tabs__image');
    const container = oldImage.parentElement;

    // Ensure container has relative positioning for absolute children
    const originalPosition = container.style.position;
    if (!originalPosition || originalPosition === 'static') {
      container.style.position = 'relative';
    }

    // Position new image on top of old image using absolute positioning
    newImage.style.position = 'absolute';
    newImage.style.top = '0';
    newImage.style.left = '0';
    newImage.style.width = '100%';
    newImage.style.height = '100%';
    newImage.style.zIndex = '2';
    newImage.hidden = false;

    if (newImgElement) {
      newImgElement.style.opacity = '0';
      newImgElement.style.transition = 'opacity 0.3s ease-in-out';
    }

    // Ensure old image stays visible underneath
    oldImage.style.position = 'relative';
    oldImage.style.zIndex = '1';

    // Force reflow to ensure new image is rendered
    void newImage.offsetWidth;

    // Fade in new image on top of old
    requestAnimationFrame(() => {
      if (newImgElement) {
        newImgElement.style.opacity = '1';
      }

      // Wait for fade in to complete, then clean up
      setTimeout(() => {
        // Hide old image
        oldImage.hidden = true;
        oldImage.style.position = '';
        oldImage.style.zIndex = '';
        
        // Reset old image opacity
        if (oldImgElement) {
          oldImgElement.style.opacity = '';
          oldImgElement.style.transition = '';
        }

        // Reset new image positioning
        newImage.style.position = '';
        newImage.style.top = '';
        newImage.style.left = '';
        newImage.style.width = '';
        newImage.style.height = '';
        newImage.style.zIndex = '';
        
        // Reset new image transition
        if (newImgElement) {
          newImgElement.style.opacity = '';
          newImgElement.style.transition = '';
        }

        // Reset container position if we set it
        if (!originalPosition || originalPosition === 'static') {
          container.style.position = '';
        }
      }, 500);
    });
  }
}

customElements.define('product-image-tabs', ProductImageTabs);
